import { Layout } from "../../components/layout/Layout";
import { PageTransition } from "../../components/layout/PageTransition";
import { SEO } from "../../components/SEO";
import Badge from "../../components/ui/Badge";
import { Calendar, Sparkles } from "lucide-react";
import styled from "styled-components";

const Section = styled.section`
  padding: 96px 16px;
  position: relative;
  overflow: hidden;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}px) {
    padding: 96px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    padding: 120px 32px;
  }
`;

const BackgroundBlobs = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(128px);
  animation: pulse-slow 4s ease-in-out infinite;

  &.blob-1 {
    top: 80px;
    right: 25%;
    width: 500px;
    height: 500px;
    background: ${({ theme }) => `${theme.colors.light?.primary || "hsl(258, 90%, 58%)"}20`};
  }

  &.blob-2 {
    bottom: 80px;
    left: 25%;
    width: 400px;
    height: 400px;
    background: ${({ theme }) => `${theme.colors.light?.accent || "hsl(16, 90%, 58%)"}20`};
    animation-delay: 2s;
  }
`;

const Container = styled.div`
  max-width: 896px;
  margin: 0 auto;
  position: relative;
  z-index: 10;
`;

const HeroContent = styled.div`
  max-width: 768px;
  margin: 0 auto;
  text-align: center;
  margin-bottom: 64px;
`;

const BadgeWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-size: ${({ theme }) => theme.typography.fontSize.h1};
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 24px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const GradientText = styled.span`
  background: ${({ theme }) => theme.colors.gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.bodyLarge};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const ChangelogList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const ChangelogItem = styled.div`
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.light?.card?.background || theme.colors.light?.background || "white"};
  border: 1px solid ${({ theme }) => theme.colors.light?.border || "hsl(240, 10%, 90%)"};

  body.dark & {
    background: ${({ theme }) => theme.colors.dark?.card?.background || theme.colors.dark?.background || "hsl(240, 15%, 10%)"};
    border-color: ${({ theme }) => theme.colors.dark?.border || "hsl(240, 15%, 18%)"};
  }
`;

const ChangelogHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ChangelogDate = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const ChangelogVersion = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.light?.primary || theme.colors.primary.light};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.primary || theme.colors.primary.light};
  }
`;

const ChangelogTitle = styled.h3`
  font-family: ${({ theme }) => theme.typography.fontFamily.display};
  font-weight: 600;
  margin: 0 0 12px 0;
  color: ${({ theme }) => theme.colors.light?.foreground || theme.colors.text.heading};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.foreground || "#ffffff"};
  }
`;

const ChangelogContent = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.light?.muted?.foreground || theme.colors.text.muted};

  body.dark & {
    color: ${({ theme }) => theme.colors.dark?.muted?.foreground || "hsl(240, 10%, 60%)"};
  }
`;

const changelogEntries = [
  {
    version: "v2.1.0",
    date: "January 15, 2026",
    title: "Enhanced Analytics Dashboard",
    content: "Added real-time metrics, custom date ranges, and export functionality. Improved performance with faster data loading.",
  },
  {
    version: "v2.0.5",
    date: "January 8, 2026",
    title: "New Integration: Stripe",
    content: "Connect your Stripe account to automatically sync payment data and manage subscriptions directly from Kliq CRM.",
  },
  {
    version: "v2.0.0",
    date: "December 20, 2025",
    title: "Major UI Redesign",
    content: "Complete redesign with improved navigation, better mobile experience, and enhanced accessibility. New dark mode support.",
  },
  {
    version: "v1.9.2",
    date: "December 5, 2025",
    title: "Performance Improvements",
    content: "Optimized database queries, reduced page load times by 40%, and improved API response times.",
  },
];

export default function Changelog() {
  return (
    <Layout>
      <SEO
        title="Changelog"
        description="Stay up to date with the latest features, improvements, and updates to Kliq CRM."
        keywords="changelog, updates, features, release notes"
        hreflang
      />
      <PageTransition>
        <Section>
          <BackgroundBlobs>
            <Blob className="blob-1" />
            <Blob className="blob-2" />
          </BackgroundBlobs>
          <Container>
            <HeroContent>
              <BadgeWrapper>
                <Badge $variant="primary">Changelog</Badge>
              </BadgeWrapper>
              <Title>
                What's <GradientText>new</GradientText>
              </Title>
              <Description>
                Stay up to date with the latest features, improvements, and updates to Kliq CRM.
              </Description>
            </HeroContent>

            <ChangelogList>
              {changelogEntries.map((entry) => (
                <ChangelogItem key={entry.version}>
                  <ChangelogHeader>
                    <ChangelogDate>
                      <Calendar size={16} />
                      {entry.date}
                    </ChangelogDate>
                    <ChangelogVersion>{entry.version}</ChangelogVersion>
                  </ChangelogHeader>
                  <ChangelogTitle>{entry.title}</ChangelogTitle>
                  <ChangelogContent>{entry.content}</ChangelogContent>
                </ChangelogItem>
              ))}
            </ChangelogList>
          </Container>
        </Section>
      </PageTransition>
    </Layout>
  );
}
